import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { collection, addDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "../../authentication/firebase";
import { Close, CheckCircle, LocalOffer } from "@mui/icons-material";
import { CircularProgress, Modal, Backdrop, Fade } from "@mui/material";

const RedeemOfferPopup = ({ isOpen, onClose, popupData, couponCode, onSuccessfulRedeem }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    shirtSize: "",
    selectedItems: {
      keychain: false,
      stickers: false,
      shirt: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (isOpen && auth.currentUser) {
      setFormData(prev => ({
        ...prev,
        email: auth.currentUser.email || "",
        firstName: auth.currentUser.displayName?.split(" ")[0] || "",
        lastName: auth.currentUser.displayName?.split(" ").slice(1).join(" ") || ""
      }));
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (itemName) => {
    setFormData(prev => ({
      ...prev,
      selectedItems: {
        ...prev.selectedItems,
        [itemName]: !prev.selectedItems[itemName]
      },
      // Clear shirt size if shirt is unchecked
      shirtSize: itemName === 'shirt' && prev.selectedItems[itemName] ? '' : prev.shirtSize
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Check if at least one item is selected
      const hasSelectedItems = Object.values(formData.selectedItems).some(selected => selected);
      if (!hasSelectedItems) {
        throw new Error("Please select at least one item to redeem.");
      }

      // Validate required fields
      const requiredFields = ["firstName", "lastName", "email", "address", "city", "state", "zipCode"];
      
      // Add shirt size to required fields only if shirt is selected
      if (formData.selectedItems.shirt) {
        requiredFields.push("shirtSize");
      }
      
      const missingFields = requiredFields.filter(field => !formData[field].trim());
      
      if (missingFields.length > 0) {
        throw new Error(`Please fill in: ${missingFields.join(", ")}`);
      }

      // Check if this email has already redeemed this offer
      const emailToCheck = auth.currentUser?.email || formData.email;
      const redeemedOffersRef = collection(db, "redeemedOffers");
      const emailQuery = query(
        redeemedOffersRef, 
        where("userEmail", "==", emailToCheck),
        where("offerType", "==", "early-bird-special")
      );
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        throw new Error("This email has already redeemed this offer. Each email can only redeem once.");
      }

      // Save to Firebase
      const offerData = {
        ...formData,
        couponCode,
        userId: auth.currentUser?.uid || null,
        userEmail: emailToCheck,
        offerType: "early-bird-special",
        popupName: popupData?.name || "Early Bird Special",
        redeemedAt: serverTimestamp(),
        status: "redeemed"
      };

      await addDoc(collection(db, "redeemedOffers"), offerData);
      setSubmitted(true);
      
      // Notify parent component that offer was successfully redeemed
      if (onSuccessfulRedeem) {
        onSuccessfulRedeem();
      }
    } catch (error) {
      console.error("Error redeeming offer:", error);
      setError(error.message || "Failed to redeem offer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const popupContent = () => {
    if (submitted) {
      return (
        <PopupContent>
          <CloseButton onClick={onClose}>
            <Close />
          </CloseButton>
          
          <PopupHeader>
            <motion.div
              className="success-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <CheckCircle style={{ fontSize: "4rem", color: "white" }} />
            </motion.div>
            
            <motion.h2
              className="popup-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              Offer Successfully Redeemed!
            </motion.h2>
          </PopupHeader>

          <PopupBody>
            <motion.div
              className="company-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h2>Everlasting</h2>
              <p>Your engagement today helps preserve tomorrow</p>
            </motion.div>

            <motion.p
              className="confirmation-note"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              You'll get your welcome package details by email shortly!
            </motion.p>

            <motion.button
              className="close-btn"
              onClick={onClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              Close
            </motion.button>
          </PopupBody>
        </PopupContent>
      );
    }

    return (
      <PopupContent>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
        
        <PopupHeader>
          <motion.h2
            className="popup-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            🎁 Redeem Your Gift
          </motion.h2>
          
          <motion.div
            className="offer-badge"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <LocalOffer style={{ marginRight: "8px" }} />
            {popupData?.name || "Early Bird Special"}
          </motion.div>
        </PopupHeader>

        <PopupBody>
          <motion.p
            className="description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Select the items you'd like to receive, then provide your shipping information for your special welcome gift box.
          </motion.p>

          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          <motion.form
            onSubmit={handleSubmit}
            className="redeem-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <div className="form-group">
              <label>Select Items *</label>
              <div className="checkbox-group">
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="keychain"
                    checked={formData.selectedItems.keychain}
                    onChange={() => handleCheckboxChange('keychain')}
                  />
                  <label htmlFor="keychain">Keychain</label>
                </div>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="stickers"
                    checked={formData.selectedItems.stickers}
                    onChange={() => handleCheckboxChange('stickers')}
                  />
                  <label htmlFor="stickers">Stickers</label>
                </div>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="shirt"
                    checked={formData.selectedItems.shirt}
                    onChange={() => handleCheckboxChange('shirt')}
                  />
                  <label htmlFor="shirt">Shirt</label>
                </div>
              </div>
            </div>

            {formData.selectedItems.shirt && (
              <div className="form-group">
                <label htmlFor="shirtSize">Shirt Size *</label>
                <select
                  id="shirtSize"
                  name="shirtSize"
                  value={formData.shirtSize}
                  onChange={handleInputChange}
                  required
                  className="shirt-size-select"
                >
                  <option value="">Choose your shirt size</option>
                  <option value="small">S - Small</option>
                  <option value="medium">M - Medium</option>
                  <option value="large">L - Large</option>
                  <option value="xl">XL - Extra Large</option>
                  <option value="2xl">2XL - Double Extra Large</option>
                </select>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Street Address *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="state">State/Province *</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="zipCode">ZIP/Postal Code *</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <motion.button
              type="submit"
              className="redeem-btn"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  Processing...
                </>
              ) : (
                <>
                  <LocalOffer />
                  Redeem My Gift Box
                </>
              )}
            </motion.button>
          </motion.form>
        </PopupBody>
      </PopupContent>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: StyledBackdrop }}
    >
      <Fade in={isOpen}>
        <ModalContainer>
          {popupContent()}
        </ModalContainer>
      </Fade>
    </Modal>
  );
};

export default RedeemOfferPopup;

const PopupContent = styled.div`
  position: relative;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 90%;
  
  @media (max-width: 768px) {
    max-width: 95%;
    width: 95%;
    margin: 0 auto;
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    max-width: 98%;
    width: 98%;
    border-radius: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }
  
  svg {
    color: var(--text-color);
  }
`;

const PopupHeader = styled.div`
  position: relative;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark-color) 100%);
  padding: 40px 20px 30px;
  text-align: center;
  
  .success-icon {
    margin-bottom: 20px;
  }
  
  .popup-title {
    color: white;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 30px 16px 25px;
    
    .popup-title {
      font-size: 1.6rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 25px 12px 20px;
    
    .popup-title {
      font-size: 1.4rem;
    }
  }
`;

const PopupBody = styled.div`
  padding: 30px;
  
  .company-title {
    text-align: center;
    margin-bottom: 25px;
    
    h2 {
      color: var(--text-color);
      font-size: 2.2rem;
      margin: 0 0 10px 0;
      font-weight: 700;
      color: var(--primary-dark-color);
    }
    
    p {
      color: var(--text-gray-color);
      margin: 0;
      font-size: 1rem;
      line-height: 1.4;
    }
  }
  
  .address-section {
    text-align: center;
    margin-bottom: 25px;
    
    .address-line {
      color: var(--text-gray-color);
      margin: 5px 0;
      font-size: 0.95rem;
      font-weight: 500;
      letter-spacing: 0.5px;
    }
  }
  
  .confirmation-note {
    font-style: italic;
    color: black;
    font-weight: 500;
    margin: 20px 0;
    line-height: 1.4;
    text-align: center;
    font-size: 0.95rem;
  }

  .description {
    color: var(--text-gray-color);
    line-height: 1.6;
    text-align: center;
    margin-bottom: 25px;
    font-size: 1.1rem;
  }

  .error-message {
    background: #fee;
    color: #c33;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 20px;
    text-align: center;
    border: 1px solid #fcc;
  }

  .redeem-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 15px;
    }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
      font-weight: 600;
      color: var(--text-color);
      font-size: 0.9rem;
    }
    
    .checkbox-group {
      display: flex;
      gap: 20px;
      margin-top: 8px;
      flex-wrap: wrap;
      
      @media (max-width: 480px) {
        flex-direction: column;
        gap: 12px;
      }
    }
    
    .checkbox-item {
      display: flex;
      align-items: center;
      gap: 8px;
      
      input[type="checkbox"] {
        width: 18px;
        height: 18px;
        accent-color: var(--primary-color);
        cursor: pointer;
      }
      
      label {
        font-weight: 500;
        font-size: 0.95rem;
        cursor: pointer;
        margin: 0;
      }
    }
    
    input {
      padding: 12px 16px;
      border: 2px solid #e1e5e9;
      border-radius: 8px;
      font-size: 1rem;
      transition: all 0.3s ease;
      
      &:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
      }
      
      &::placeholder {
        color: #999;
      }
    }
    
    .shirt-size-select {
      padding: 12px 16px;
      border: 2px solid #e1e5e9;
      border-radius: 8px;
      font-size: 1rem;
      transition: all 0.3s ease;
      background-color: white;
      cursor: pointer;
      height: 48px;
      line-height: 1.2;
      min-height: 48px;
      width: 100%;
      box-sizing: border-box;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-size: 16px;
      padding-right: 40px;
      
      &:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
      }
      
      option {
        padding: 12px 16px;
        font-size: 1rem;
        line-height: 1.2;
        height: auto;
      }
    }
  }

  .redeem-btn {
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark-color) 100%);
    color: white;
    border: none;
    padding: 18px 30px;
    border-radius: 50px;
    font-weight: 700;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    margin-top: 10px;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
    }
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .close-btn {
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark-color) 100%);
    color: white;
    border: none;
    padding: 16px 24px;
    border-radius: 50px;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: block;
    width: 100%;
    min-height: 50px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }
  }
  
  @media (max-width: 768px) {
    padding: 25px 20px;
    
    .company-title {
      margin-bottom: 20px;
      
      h2 {
        font-size: 2rem;
      }
      
      p {
        font-size: 0.95rem;
      }
    }
    
    .address-section {
      margin-bottom: 20px;
    }
    
    .confirmation-note {
      font-size: 0.9rem;
      margin: 18px 0;
    }
    
    .close-btn {
      padding: 14px 20px;
      font-size: 0.95rem;
    }
    
    .shirt-size-select {
      height: 44px;
      min-height: 44px;
      font-size: 0.95rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 20px 16px;
    
    .company-title {
      margin-bottom: 18px;
      
      h2 {
        font-size: 1.8rem;
      }
      
      p {
        font-size: 0.9rem;
      }
    }
    
    .address-section {
      margin-bottom: 18px;
      
      .address-line {
        font-size: 0.9rem;
      }
    }
    
    .confirmation-note {
      font-size: 0.85rem;
      margin: 16px 0;
    }
    
    .close-btn {
      padding: 12px 18px;
      font-size: 0.9rem;
      min-height: 48px;
    }
    
    .shirt-size-select {
      height: 42px;
      min-height: 42px;
      font-size: 0.9rem;
      padding: 10px 14px;
      padding-right: 36px;
    }
  }
`;

const StyledBackdrop = styled(Backdrop)`
  background-color: rgb(0 0 0 / 0.5);
`;

const ModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  outline: none;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-height: 95vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    max-height: 98vh;
    padding: 10px;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;
